(() => {
  const initialized = new WeakSet();
  const activeSections = new Set();
  const sectionSyncers = new WeakMap();
  // Tracks the scroll/resize handlers registered for the IntersectionObserver fallback path
  // so they can be removed from window when the section is unloaded by the Theme Editor.
  const sectionListeners = new WeakMap();

  const setPriceContent = (el, value) => {
    if (!el) return;
    el.innerHTML = value || "";
  };

  const getUnavailableLabel = () => {
    if (!window.theme || !window.theme.strings) return "Nicht verfuegbar";
    return window.theme.strings.noStock || "Nicht verfuegbar";
  };

  const getVariantIdFromEvent = (event) => {
    if (!event.detail || !event.detail.variant) return null;
    return event.detail.variant.id ? String(event.detail.variant.id) : null;
  };

  const initSection = (section) => {
    if (!section || initialized.has(section)) return;

    const variantDataEl = section.querySelector('[id^="TPGProductVariants-"]');
    if (!variantDataEl) return;

    let variants = {};
    try {
      variants = JSON.parse(variantDataEl.textContent);
    } catch (error) {
      return;
    }

    const form = section.querySelector(".js-product-form-main");
    const variantInputSource = form || section.querySelector(".tpg-sticky-atc__form");

    const addButton = form ? form.querySelector('[name="add"]') : null;
    const addButtonIcon = addButton ? addButton.querySelector("svg")?.outerHTML || "" : "";
    const stickyCards = section.querySelectorAll("[data-tpg-sticky-atc]");
    const availabilityWrap = section.querySelector("[data-tpg-availability]");
    const availabilityStatus = section.querySelector("[data-tpg-availability-status]");
    const availabilityNote = section.querySelector("[data-tpg-availability-note]");
    const wishlistButtons = section.querySelectorAll("[abrwl-button][data-product-id]");

    initialized.add(section);

    const getVariant = () => {
      if (!variantInputSource) return null;

      const input = variantInputSource.querySelector('input[name="id"]');
      return input ? variants[input.value] : null;
    };

    const updateAvailability = (variant) => {
      if (!availabilityWrap || !availabilityStatus || !availabilityNote) return;

      const lowThreshold = Number(availabilityWrap.dataset.lowThreshold || 5);
      let state = "available";
      let label = availabilityWrap.dataset.textAvailable || "Auf Lager";
      const countSuffix = availabilityWrap.dataset.textCountSuffix || "Stk. verfuegbar";

      if (!variant || !variant.available) {
        state = "unavailable";
        label = availabilityWrap.dataset.textUnavailable || "Nicht verfuegbar";
      } else if (variant.inventory_management && variant.inventory_policy !== "continue" && variant.inventory_quantity <= lowThreshold) {
        state = "low";
        label = availabilityWrap.dataset.textLow || "Nur noch wenige verfuegbar";
      }

      availabilityStatus.className = `tpg-main-product__availability-status tpg-main-product__availability--${state}`;
      availabilityStatus.textContent = label;
      availabilityNote.textContent = variant && variant.available && variant.inventory_quantity > 0 ? `${variant.inventory_quantity} ${countSuffix}` : "";
    };

    const updateMainAddButton = (variant) => {
      if (!addButton) return;

      const labelEl = addButton.querySelector(".tpg-main-product__atc-label");
      const priceEl = addButton.querySelector("[data-tpg-atc-price]");
      const cartLabel = addButton.dataset.addToCartText || "In den Warenkorb";
      const buttonLabel = !variant || !variant.available ? getUnavailableLabel() : cartLabel;
      const buttonPrice = variant && variant.available && variant.button_price ? variant.button_price : "";

      if (!labelEl || !priceEl) {
        // Server-rendered button is missing the expected child spans (legacy markup).
        // Rebuild the full structure including both the label and the price span so that:
        // (a) setTaxAwareContent has a real element to write the price into, and
        // (b) subsequent calls to updateMainAddButton find [data-tpg-atc-price] via the normal path.
        addButton.innerHTML = `${addButtonIcon}
          <span class="tpg-main-product__atc-label">${buttonLabel}</span>`;
        const rebuiltPriceEl = addButton.querySelector("[data-tpg-atc-price]");
        setPriceContent(rebuiltPriceEl, buttonPrice);
        if (rebuiltPriceEl) {
          rebuiltPriceEl.hidden = !buttonPrice;
        }
        return;
      }

      labelEl.textContent = buttonLabel;
      setPriceContent(priceEl, buttonPrice);
      priceEl.hidden = !buttonPrice;
    };

    const updateWishlistButtons = (variant) => {
      if (!variant) return;
      wishlistButtons.forEach((button) => {
        button.setAttribute("data-variant-id", variant.id);
      });
    };

    const updateStickyAtc = (variant) => {
      if (!stickyCards.length) return;

      stickyCards.forEach((card) => {
        const image = card.querySelector(".tpg-sticky-atc__image");
        const price = card.querySelector("[data-tpg-sticky-price]");
        const compare = card.querySelector("[data-tpg-sticky-compare]");
        const compareValue = card.querySelector("[data-tpg-sticky-compare-value]");
        const input = card.querySelector('input[name="id"]');
        const button = card.querySelector('[name="add"]');
        const label = card.querySelector(".tpg-sticky-atc__button-label");

        card.classList.toggle("is-available", !!(variant && variant.available));
        card.classList.toggle("has-compare-price", !!(variant && variant.has_compare_at_price));

        if (input && variant) input.value = variant.id;

        if (image && variant && variant.image) {
          image.setAttribute("src", variant.image);
          image.setAttribute("alt", variant.image_alt || "");
        }

        if (price) {
          price.classList.toggle("tpg-sticky-atc__price--sale", !!(variant && variant.has_compare_at_price));
          setPriceContent(
            price,
            variant && variant.button_price ? variant.button_price : "",
          );
        }

        if (compare && compareValue) {
          compare.hidden = !(variant && variant.has_compare_at_price);
          setPriceContent(
            compareValue,
            variant && variant.compare_at_price ? variant.compare_at_price : "",
          );
        }

        if (button) {
          const cartLabel = button.dataset.addToCartText || "In den Warenkorb";
          button.disabled = !variant || !variant.available;
          if (label) label.textContent = !variant || !variant.available ? getUnavailableLabel() : cartLabel;
        }
      });
    };

    const sync = (variantOverride) => {
      const variant = variantOverride || getVariant();
      updateAvailability(variant);
      updateMainAddButton(variant);
      updateWishlistButtons(variant);
      updateStickyAtc(variant);
    };

    const setStickyVisibility = (visible) => {
      stickyCards.forEach((card) => {
        card.classList.toggle("is-visible", visible);
      });
    };

    if (stickyCards.length) {
      const normalAtcButton = section.querySelector(".tpg-main-product__atc-button");
      if (normalAtcButton && "IntersectionObserver" in window) {
        // Preferred: IntersectionObserver fires only when visibility actually changes,
        // with no per-scroll overhead and no manual cleanup needed.
        const observer = new IntersectionObserver(
          ([entry]) => {
            setStickyVisibility(!entry.isIntersecting);
          },
          { threshold: 0.01 },
        );
        observer.observe(normalAtcButton);
      } else if (normalAtcButton) {
        // Fallback for browsers without IntersectionObserver: poll on scroll/resize.
        // The handler is stored in sectionListeners so it can be removed from window
        // when the section is unloaded (see shopify:section:unload below).
        const checkVisibility = () => {
          const rect = normalAtcButton.getBoundingClientRect();
          setStickyVisibility(rect.bottom < 0 || rect.top > window.innerHeight);
        };
        checkVisibility();
        window.addEventListener("scroll", checkVisibility, { passive: true });
        window.addEventListener("resize", checkVisibility);
        sectionListeners.set(section, checkVisibility);
      }
    }

    activeSections.add(section);
    sectionSyncers.set(section, () => sync());

    if (variantInputSource) requestAnimationFrame(() => sync());
    section.addEventListener("change", (event) => {
      if (event.target && event.target.name === "id") sync();
    });
    section.addEventListener("on:variant:change", (event) => {
      const variantId = getVariantIdFromEvent(event);
      sync(variantId ? variants[variantId] : null);
    });
  };

  const init = (root = document) => {
    if (root.matches && root.matches(".section-main-product-tpg")) initSection(root);
    root.querySelectorAll(".section-main-product-tpg").forEach(initSection);
  };

  init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    activeSections.delete(event.target);
    sectionSyncers.delete(event.target);
    // Remove the scroll/resize fallback handlers if they were registered for this section.
    // Without this, repeated Theme Editor load/unload cycles accumulate stale window listeners
    // that continue to run even after the section's DOM has been removed.
    const handler = sectionListeners.get(event.target);
    if (handler) {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
      sectionListeners.delete(event.target);
    }
  });
})();
